'use client'

import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { Project } from '@/lib/projects-data'
import { Tag } from './tag'
import { trackEvent } from '@/lib/utils'

interface ProjectCardProps {
  project: Project
  index: number
}

export function ProjectCard({ project, index }: ProjectCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
    >
      <Link 
        href={`/projects/${project.slug}`} 
        className="group block"
        onClick={() => {
          trackEvent('project_card_clicked', {
            event_category: 'engagement',
            event_label: project.slug,
            project_title: project.title,
            value: 1,
          })
        }}
      >
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all h-full flex flex-col">
          <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
            <Image
              src={project.thumbnail}
              alt={project.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          <div className="p-6 flex flex-col flex-grow">
            <div className="mb-2">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">
                {project.title}
              </h3>
              <p className="text-sm font-medium text-blue-600">{project.role}</p>
            </div>
            <p className="text-gray-600 mb-4 flex-grow line-clamp-3">
              {project.shortDescription}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {project.skills.slice(0, 3).map((skill, idx) => (
                <Tag key={idx} size="sm" variant="outline">
                  {skill}
                </Tag>
              ))}
              {project.skills.length > 3 && (
                <Tag size="sm" variant="outline">
                  +{project.skills.length - 3}
                </Tag>
              )}
            </div>
            <button className="w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors">
              View Case Study
            </button>
          </div>
        </div>
      </Link>
      </motion.div>
  )
}

